import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Plane, Navigation, Info, AlertCircle, Check } from 'lucide-react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

// US States list for dropdown
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

/**
 * Location Settings - FINAL LOCKED DIRECTIVE
 * 
 * Two location states:
 * 1) Base Location (default) - Required, private, defines discovery lens
 * 2) Travel Mode (override) - OFF by default, temporary location override
 * 
 * Rules:
 * - No per-town toggling inside Base State
 * - No background tracking when Travel Mode is OFF
 * - No broadcasting exact location to other users
 * - Location affects DISCOVERY only, not identity or visibility
 */
export default function LocationSettings() {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Base Location
  const [baseState, setBaseState] = useState('');
  const [baseTown, setBaseTown] = useState('');
  
  // Travel Mode
  const [travelMode, setTravelMode] = useState(false);
  const [travelLocation, setTravelLocation] = useState(null);
  const [travelStartedAt, setTravelStartedAt] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);

  useEffect(() => {
    fetchLocationSettings();
  }, []);

  const fetchLocationSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/user/location`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data) {
        setBaseState(res.data.base_location?.state || '');
        setBaseTown(res.data.base_location?.town || '');
        setTravelMode(res.data.travel_mode?.enabled || false);
        setTravelLocation(res.data.travel_mode?.location || null);
        setTravelStartedAt(res.data.travel_mode?.started_at || null);
      }
    } catch (error) {
      console.error('Error fetching location settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveBaseLocation = async () => {
    if (!baseState) {
      toast.error('Please select your base state');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/user/location`, {
        base_state: baseState,
        base_town: baseTown || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local user state
      setUser(prev => ({ ...prev, base_state: baseState, base_town: baseTown }));
      toast.success('Base location saved');
    } catch (error) {
      toast.error('Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const enableTravelMode = async () => {
    setGettingLocation(true);
    
    // Request location permission
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      setGettingLocation(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // Reverse geocode to get state/city
          const { latitude, longitude } = position.coords;
          
          // Use a simple reverse geocoding approach
          // In production, you'd use a proper geocoding API
          const geoRes = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const geoData = await geoRes.json();
          
          const state = geoData.address?.state || '';
          const city = geoData.address?.city || geoData.address?.town || geoData.address?.village || '';
          
          const token = localStorage.getItem('token');
          await axios.post(`${API}/api/user/travel-mode/enable`, {
            state: state,
            town: city,
            lat: latitude,
            lng: longitude
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setTravelMode(true);
          setTravelLocation({ state, town: city, lat: latitude, lng: longitude });
          setTravelStartedAt(new Date().toISOString());
          setUser(prev => ({ ...prev, travel_mode_enabled: true }));
          
          toast.success(`Travel Mode enabled in ${state}`);
        } catch (error) {
          console.error('Travel mode error:', error);
          toast.error('Failed to enable Travel Mode');
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        setGettingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          toast.error('To use Travel Mode, enable Location Services in your browser settings.');
        } else {
          toast.error('Failed to get your location');
        }
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const disableTravelMode = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/user/travel-mode/disable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTravelMode(false);
      setTravelLocation(null);
      setTravelStartedAt(null);
      setUser(prev => ({ ...prev, travel_mode_enabled: false }));
      
      toast.success('Travel Mode disabled. Back to base location.');
    } catch (error) {
      toast.error('Failed to disable Travel Mode');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--lk-sage)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            <button onClick={() => navigate('/settings')} className="p-2 -ml-2 rounded-lg hover:opacity-70 transition-opacity">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Location</h1>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        
        {/* Travel Mode Card (if active) */}
        {travelMode && (
          <Card className="p-4 border-2" style={{ backgroundColor: 'var(--lk-sage-muted)', borderColor: 'var(--lk-sage)' }}>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full" style={{ backgroundColor: 'var(--lk-sage)' }}>
                <Plane className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
                  Travel Mode Active
                </p>
                <p className="text-sm mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
                  Currently in: <strong>{travelLocation?.state}</strong>
                  {travelLocation?.town && `, ${travelLocation.town}`}
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
                  Discovery uses your travel location
                </p>
              </div>
              <Button
                onClick={disableTravelMode}
                variant="outline"
                size="sm"
                className="shrink-0"
              >
                Turn Off
              </Button>
            </div>
          </Card>
        )}

        {/* Base Location Section */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Base Location</h2>
          </div>
          
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Your home location defines your default discovery lens. You can browse, discover, and participate anywhere within your base state without Travel Mode.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                Base State <span className="text-red-500">*</span>
              </label>
              <select
                value={baseState}
                onChange={(e) => setBaseState(e.target.value)}
                className="w-full p-3 rounded-lg border"
                style={{ 
                  backgroundColor: 'var(--fy-background-primary)', 
                  borderColor: 'var(--fy-border-subtle)',
                  color: 'var(--fy-text-primary)'
                }}
              >
                <option value="">Select your state</option>
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                Home Town / Zip (optional)
              </label>
              <input
                type="text"
                value={baseTown}
                onChange={(e) => setBaseTown(e.target.value)}
                placeholder="e.g., Portland or 04101"
                className="w-full p-3 rounded-lg border"
                style={{ 
                  backgroundColor: 'var(--fy-background-primary)', 
                  borderColor: 'var(--fy-border-subtle)',
                  color: 'var(--fy-text-primary)'
                }}
              />
            </div>

            <Button
              onClick={saveBaseLocation}
              disabled={saving || !baseState}
              className="w-full"
              style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
            >
              {saving ? 'Saving...' : 'Save Base Location'}
            </Button>
          </div>

          <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5" style={{ color: 'var(--fy-text-disabled)' }} />
              <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                Your base location is <strong>private</strong> and never broadcast to other users. It only affects what you see in discovery.
              </p>
            </div>
          </div>
        </Card>

        {/* Travel Mode Section */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Plane className="w-5 h-5" style={{ color: 'var(--lk-terra)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Travel Mode</h2>
          </div>

          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            When you're away from home, enable Travel Mode to discover content and communities based on your current location.
          </p>

          <div className="space-y-4">
            {!travelMode ? (
              <Button
                onClick={enableTravelMode}
                disabled={gettingLocation}
                className="w-full"
                style={{ backgroundColor: 'var(--lk-terra)', color: 'white' }}
              >
                <Navigation className="w-4 h-4 mr-2" />
                {gettingLocation ? 'Getting Location...' : 'Enable Travel Mode'}
              </Button>
            ) : (
              <Button
                onClick={disableTravelMode}
                variant="outline"
                className="w-full"
              >
                <Check className="w-4 h-4 mr-2" />
                Disable Travel Mode
              </Button>
            )}
          </div>

          {/* How Travel Mode Works */}
          <div className="mt-6 space-y-3">
            <h3 className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              How Travel Mode Works
            </h3>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              <li className="flex items-start gap-2">
                <span className="text-lg">📍</span>
                <span>When ON, discovery uses your current travel location</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lg">🏷️</span>
                <span>You'll be labeled as "Traveler" (subtle indicator only)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lg">🏠</span>
                <span>Turn OFF to instantly revert to your base location</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lg">🔒</span>
                <span>No background tracking when Travel Mode is OFF</span>
              </li>
            </ul>
          </div>

          <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: 'var(--fy-text-disabled)' }} />
              <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                Travel Mode requires Location Services permission. Your exact location is never shared with other users.
              </p>
            </div>
          </div>
        </Card>

        {/* Base State Roaming Rule */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
          <h3 className="font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
            Base State Roaming
          </h3>
          <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            You can browse, discover, post, and participate anywhere within your base state ({baseState || 'not set'}) without enabling Travel Mode.
          </p>
          <p className="text-sm mt-2" style={{ color: 'var(--fy-text-secondary)' }}>
            Travel Mode is only needed when you're visiting a different state.
          </p>
        </Card>

      </main>
    </div>
  );
}
